import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  CircularProgress,
  Grid,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";
import NewEventDialog from "../../../components/NewEventDialog/NewEventDialog";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const LeagueSettings = ({ leagueEvent, onLeagueEventUpdate }) => {
  const [saving, setSaving] = useState(false);
  const [editEventOpen, setEditEventOpen] = useState(false);

  // Event-level fields
  const [eventName, setEventName] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // LeagueEvent-level fields
  const [sportType, setSportType] = useState("");
  const [fields, setFields] = useState([]);
  const [newFieldInput, setNewFieldInput] = useState("");
  const [maxTeamSize, setMaxTeamSize] = useState("");
  const [standingsMode, setStandingsMode] = useState("realtime");

  // Question Group (event-level, persisted immediately on change)
  const [questionGroups, setQuestionGroups] = useState([]);
  const [selectedQuestionGroup, setSelectedQuestionGroup] = useState("");

  useEffect(() => {
    if (!leagueEvent) return;
    setEventName(leagueEvent.event?.name || "");
    setStartDate(leagueEvent.event?.start ? dayjs(leagueEvent.event.start) : null);
    setEndDate(leagueEvent.event?.end ? dayjs(leagueEvent.event.end) : null);
    setSportType(leagueEvent.sportType || "");
    setFields(leagueEvent.fields || []);
    setMaxTeamSize(leagueEvent.maxTeamSize ?? "");
    setStandingsMode(leagueEvent.standingsMode || "realtime");
    loadQuestionGroups();
  }, [leagueEvent]);

  const loadQuestionGroups = async () => {
    const orgId = leagueEvent?.event?.org;
    const eventId = leagueEvent?.event?._id;
    if (!orgId || !eventId) return;
    const groupsResult = await request(`questiongroups?orgId=${orgId}`);
    if (groupsResult?.success) {
      setQuestionGroups(groupsResult.questionGroups || []);
    }
    const eqgResult = await request(
      `questiongroups/eventquestiongroup?eventId=${eventId}`,
    );
    if (eqgResult?.success && eqgResult?.eventQuestionGroup?.questionGroup) {
      setSelectedQuestionGroup(eqgResult.eventQuestionGroup.questionGroup);
    } else {
      setSelectedQuestionGroup("");
    }
  };

  const handleQuestionGroupChange = async (questionGroupId) => {
    const eventId = leagueEvent?.event?._id;
    if (!eventId || !questionGroupId) return;
    const result = await request("questiongroups/assign", "post", {
      eventId,
      questionGroupId,
    });
    if (result?.success) {
      setSelectedQuestionGroup(questionGroupId);
      toast.success("Question group assigned");
    } else {
      toast.error(result?.error || "Failed to assign question group");
    }
  };

  const addField = () => {
    if (!newFieldInput.trim()) return;
    setFields((prev) => [...prev, newFieldInput.trim()]);
    setNewFieldInput("");
  };

  const removeField = (idx) => {
    setFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);

    const eventId = leagueEvent.event?._id;
    const leagueEventId = leagueEvent._id;
    let allOk = true;

    // Update event-level fields (name, start, end)
    const eventUpdate = {};
    if (eventName.trim() !== leagueEvent.event?.name) {
      eventUpdate.name = eventName.trim();
    }
    eventUpdate.start = startDate ? startDate.toISOString() : null;
    eventUpdate.end = endDate ? endDate.toISOString() : null;

    const eventResult = await request("events/update", "post", {
      eventId,
      update: eventUpdate,
    });
    if (!eventResult?.success) {
      allOk = false;
      toast.error(eventResult?.error || "Failed to update event");
    }

    // Update league settings
    const result = await request("leagueevent/update", "post", {
      leagueEventId,
      sportType: sportType || undefined,
      fields,
      maxTeamSize: maxTeamSize !== "" ? Number(maxTeamSize) : undefined,
      standingsMode,
    });
    if (!result?.success) {
      allOk = false;
      toast.error(result?.error || "Failed to update league settings");
    }

    if (allOk) {
      toast.success("Settings saved");
      const refreshed = await request(`leagueevent?eventId=${eventId}`);
      if (refreshed?.success && onLeagueEventUpdate) {
        onLeagueEventUpdate(refreshed.data);
      }
    }

    setSaving(false);
  };

  if (!leagueEvent) return null;

  return (
    <Box sx={{ p: 3, maxWidth: 600 }}>
      {/* Event details section */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Event</Typography>
        <Button
          variant="contained"
          size="small"
          onClick={() => setEditEventOpen(true)}
        >
          Edit Event
        </Button>
      </Box>

      <TextField
        label="Event Name"
        value={eventName}
        onChange={(e) => setEventName(e.target.value)}
        fullWidth
        sx={{ mb: 3 }}
      />

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <DatePicker
              label="Season Start"
              value={startDate}
              onChange={(v) => setStartDate(v)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={6}>
            <DatePicker
              label="Season End"
              value={endDate}
              onChange={(v) => setEndDate(v)}
              minDate={startDate || undefined}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>

      <Divider sx={{ mb: 3 }} />

      {/* League settings section */}
      <Typography variant="h6" sx={{ mb: 2 }}>League Settings</Typography>

      <TextField
        label="Sport Type"
        placeholder="e.g. Kickball, Soccer, Basketball"
        value={sportType}
        onChange={(e) => setSportType(e.target.value)}
        fullWidth
        sx={{ mb: 3 }}
      />

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>Playing Fields</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {fields.map((field, idx) => (
            <Chip key={idx} label={field} onDelete={() => removeField(idx)} />
          ))}
          {fields.length === 0 && (
            <Typography variant="caption" color="text.secondary">No fields added</Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            label="Add Field"
            value={newFieldInput}
            onChange={(e) => setNewFieldInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addField(); }}
            sx={{ width: 220 }}
          />
          <IconButton size="small" onClick={addField}>
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      <TextField
        label="Max Team Size"
        type="number"
        inputProps={{ step: 1, min: 1 }}
        value={maxTeamSize}
        onChange={(e) => setMaxTeamSize(e.target.value)}
        sx={{ mb: 3, width: 160 }}
        helperText="Leave blank for no limit"
      />

      <Box sx={{ mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel>Standings Mode</InputLabel>
          <Select
            value={standingsMode}
            label="Standings Mode"
            onChange={(e) => setStandingsMode(e.target.value)}
          >
            <MenuItem value="realtime">
              Live — standings update automatically as scores are recorded
            </MenuItem>
            <MenuItem value="manual">
              Manual — standings only become visible after you publish them
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel>Question Group</InputLabel>
          <Select
            value={selectedQuestionGroup}
            label="Question Group"
            onChange={(e) => handleQuestionGroupChange(e.target.value)}
          >
            {questionGroups.map((g) => (
              <MenuItem key={g._id} value={g._id}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          Questions from this group can be required during checkout (e.g. shirt size, player level). Manage which questions are asked in the event's Questions tab.
        </Typography>
      </Box>

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving || !eventName.trim()}
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
      >
        {saving ? "Saving…" : "Save Settings"}
      </Button>

      <NewEventDialog
        open={editEventOpen}
        close={() => setEditEventOpen(false)}
        eventData={leagueEvent.event}
      />
    </Box>
  );
};

export default LeagueSettings;
